// Account (/) — profile: email, member-since, referral code used (all read-only
// except email). Changing email goes through a Google-verified re-link
// (POST /me/email/change-start → Google consent → GET /auth/callback) rather
// than a direct edit — the identity provider keys login by email, so an
// unverified change could split the account or hijack another user's. The
// outcome comes back as `?email_change=ok|conflict|denied` on this same URL.
// Card anatomy: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT
// (header + title, then content); key/value rows via DescriptionList.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass, textLinkClass } from "@ahcut/ui/recipes";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
  type AlertTone,
} from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CopyButton } from "@/components/CopyButton";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { useAuth } from "@/lib/auth";
import { ApiError, apiPost } from "@/lib/api";
import { getMe, getReferral, startEmailChange } from "@/lib/account";
import { toError } from "@/lib/errors";
import { MARKETING_BASE } from "@/lib/config";
import { SUPPORT_EMAIL, supportMailto } from "@/lib/support";
import {
  formatDate,
  formatRupiah,
  referralCommissionStatusLabel,
  referralCommissionStatusTone,
} from "@/lib/format";
import type { ReferralSummary, User } from "@/lib/types";

type EmailChangeOutcome = "ok" | "conflict" | "denied";

/** Heading of the commission history; names both its table and its phone list. */
const COMMISSIONS_HEADING_ID = "riwayat-komisi";

/** One-shot banner copy for each `?email_change=` outcome (unrecognized
 *  values render nothing — e.g. a stale/edited link). */
const EMAIL_CHANGE_BANNERS: Record<EmailChangeOutcome, { tone: AlertTone; title: string; message: string }> = {
  ok: { tone: "ok", title: "Berhasil", message: "Email berhasil diganti." },
  conflict: {
    tone: "danger",
    title: "Gagal",
    message: "Email itu sudah dipakai akun lain — tidak ada yang berubah.",
  },
  denied: { tone: "neutral", title: "Dibatalkan", message: "Penggantian email dibatalkan." },
};

export function Account() {
  const { data: user, loading, error, reload } = useAsync<User>((signal) => getMe(signal), []);
  const [params, setParams] = useSearchParams();
  const [emailChangeBanner, setEmailChangeBanner] = useState<EmailChangeOutcome | null>(null);

  // The Google re-link redirect lands back here with ?email_change=<outcome> —
  // it must be snapshotted into state BEFORE the param is stripped. Reading
  // params.get() straight from render raced the strip below: this effect (and
  // its reload()) fires while the page is still behind the `loading`
  // early-return, so by the first render that could reach the banner JSX the
  // param was already gone and the banner never painted. A real outcome needs
  // the email refetched (only `ok` actually changed it) and, either way, the
  // param stripped so a refresh or Back never replays it. The ref guard makes
  // the effect run once per mount even when StrictMode double-invokes effects,
  // so the outcome is never read twice nor reload() fired twice.
  const emailChangeHandled = useRef(false);
  useEffect(() => {
    if (emailChangeHandled.current) return;
    emailChangeHandled.current = true;
    const outcome = params.get("email_change");
    if (!outcome) return;
    if (outcome === "ok" || outcome === "conflict" || outcome === "denied") {
      setEmailChangeBanner(outcome);
      if (outcome === "ok") reload();
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("email_change");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const banner = emailChangeBanner ? EMAIL_CHANGE_BANNERS[emailChangeBanner] : undefined;
  // Rendered in every state so the screen always has its h1 (ui/DESIGN.md §10).
  const header = <PageHeader title="Akun" lede="Profil dan info akun ahcut kamu." />;

  if (loading) {
    return (
      <>
        {header}
        <Loading label="Memuat akun…" />
      </>
    );
  }
  if (error || !user) {
    return (
      <>
        {header}
        <ErrorState error={error ?? new Error("Data akun tidak tersedia.")} onRetry={reload} />
      </>
    );
  }

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      {header}
      {banner && (
        <Alert
          tone={banner.tone}
          title={banner.title}
          role={banner.tone === "danger" ? "alert" : "status"}
        >
          {banner.message}
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Profil</CardTitle>
        </CardHeader>
        <DescriptionList>
          <DescriptionTerm>Email</DescriptionTerm>
          <DescriptionDetails>{user.email}</DescriptionDetails>
          <DescriptionTerm>Anggota sejak</DescriptionTerm>
          <DescriptionDetails className={dataClass}>{formatDate(user.created_at)}</DescriptionDetails>
          <DescriptionTerm>Direferalkan oleh</DescriptionTerm>
          <DescriptionDetails>
            {user.referred_by_code ? (
              <span className={dataClass}>{user.referred_by_code}</span>
            ) : (
              <span className="text-fg-2">—</span>
            )}
          </DescriptionDetails>
          <DescriptionTerm>ID akun</DescriptionTerm>
          <DescriptionDetails className={cn(dataClass, "text-sm text-fg-2")}>{user.id}</DescriptionDetails>
        </DescriptionList>
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <p className="text-sm text-fg-1">
            Email dipakai untuk masuk. Ganti email dilakukan dengan login ke akun Google yang baru —
            lisensi, pembelian, dan komisi ikut pindah.
          </p>
          <EmailChangeControl />
        </div>
      </Card>
      <ReferralCard />
      <SecurityCard />
    </>
  );
}

/** "Ganti email" — starts the Google re-link (docs: startEmailChange). This
 *  is a FULL navigation, not a fetch: Google itself must render the consent
 *  screen, so success ends with `window.location.assign`, never a return to
 *  this component. `returnTo` is this exact route (origin+pathname, no query)
 *  so the outcome lands back on Account for the effect above to read. */
function EmailChangeControl() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const returnTo = window.location.origin + window.location.pathname;
      const authorizeUrl = await startEmailChange(returnTo);
      window.location.assign(authorizeUrl);
      // No setBusy(false) here — the browser is navigating away on success;
      // staying disabled avoids a flash of the idle button mid-redirect.
    } catch (err) {
      setError(toError(err));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button variant="quiet" onClick={() => void start()} disabled={busy}>
          {busy ? "Menyiapkan…" : "Ganti email"}
        </Button>
      </div>
      {/* Inline variant: it sits inside the Profile card, which must not nest a danger panel. */}
      {error && <ErrorState variant="inline" error={error} live />}
    </div>
  );
}

/** "Keluar dari semua perangkat" — POST /auth/logout-all revokes EVERY session
 *  server-side (all devices, this one included) and, when the relay is on, every
 *  AI connection too (grants, API keys, relay computers — owner decision 17, D45); then
 *  logout() clears the local in-memory token (its best-effort /auth/logout may
 *  already be revoked — it swallows that) and we land on /login. */
function SecurityCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function logoutAll() {
    setBusy(true);
    setError(null);
    try {
      await apiPost<null>("/auth/logout-all");
    } catch (err) {
      // Server refused. Either nothing was revoked, or the sessions went and the
      // AI connections did not; both steps are idempotent, so stay put, say so,
      // and let the person try again with the access token still in hand.
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
      return;
    }
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Keamanan</CardTitle>
      </CardHeader>
      <p className="text-sm text-fg-1">
        Keluar dari semua perangkat mencabut seluruh sesi login akun ini — termasuk sesi di
        browser ini — dan semua akses asisten AI di halaman Koneksi AI. Kamu perlu masuk ulang
        setelahnya.
      </p>
      <div>
        <Button
          variant="danger"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          <Glyph name="logOut" />
          Keluar dari semua perangkat
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Keluar dari semua perangkat?"
        confirmLabel="Keluar semua"
        danger
        busy={busy}
        onConfirm={logoutAll}
        onCancel={() => setConfirming(false)}
      >
        <p>
          Seluruh sesi login (semua browser dan perangkat) dicabut seketika, begitu juga semua
          klien AI, kunci API, dan komputer yang tersambung ke relay. Sesi ini ikut berakhir dan kamu
          diarahkan ke halaman masuk.
        </p>
        {error && (
          <Alert tone="danger" role="alert">
            {error}
          </Alert>
        )}
      </ConfirmDialog>
    </Card>
  );
}

/** Own referral card — an independent `useAsync` so a slow/failed /me/referral
 *  fetch never blocks the profile card above (its loading and error states
 *  render inside this card, the error as ErrorState's inline variant). */
function ReferralCard() {
  const {
    data: referral,
    loading,
    error,
    reload,
  } = useAsync<ReferralSummary>((signal) => getReferral(signal), []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral</CardTitle>
        <CardDescription>
          Orang yang mendaftar lewat link referral kamu tercatat di sini.
        </CardDescription>
      </CardHeader>
      {loading ? (
        <Loading label="Memuat referral…" />
      ) : error ? (
        // Not live: this card failing leaves the rest of the page usable, so it is
        // not announced as an interruption.
        <ErrorState variant="inline" error={error} onRetry={reload} />
      ) : !referral ? (
        <p className="text-sm text-fg-2">Data referral tidak tersedia.</p>
      ) : (
        <ReferralBody referral={referral} />
      )}
    </Card>
  );
}

function ReferralBody({ referral }: { referral: ReferralSummary }) {
  const link = `${MARKETING_BASE}/?ref=${referral.code}`;

  return (
    <>
      <div className="flex flex-col gap-4 rounded-lg border border-line bg-surface-1 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm text-fg-2">Kode referral kamu</p>
          <p className={cn(dataClass, "text-2xl text-fg-0")}>{referral.code}</p>
          <p className={cn(dataClass, "text-sm break-all text-fg-2")}>{link}</p>
        </div>
        <div className="shrink-0">
          <CopyButton value={link} label="Salin link" copiedLabel="Tersalin" />
        </div>
      </div>

      <DescriptionList>
        <DescriptionTerm>Pendaftar lewat kodemu</DescriptionTerm>
        <DescriptionDetails>
          <span className={dataClass}>{referral.signup_count}</span> orang
        </DescriptionDetails>
        <DescriptionTerm>Komisi terkumpul</DescriptionTerm>
        <DescriptionDetails className={dataClass}>{formatRupiah(referral.total_accrued)}</DescriptionDetails>
        <DescriptionTerm>Komisi dibayar</DescriptionTerm>
        <DescriptionDetails className={dataClass}>{formatRupiah(referral.total_paid)}</DescriptionDetails>
      </DescriptionList>

      {referral.commissions.length === 0 ? (
        <p className="text-sm text-fg-2">
          Belum ada komisi tercatat.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <h3 id={COMMISSIONS_HEADING_ID} className="text-sm font-medium text-fg-0">
              Riwayat komisi
            </h3>
            {/* ≥640px a table; below it the same rows stack as label-over-value
                items (ui/DESIGN.md §5, the /purchases pattern), so no badge is clipped. */}
            <TableWrapper label="Riwayat komisi" className="hidden sm:block">
              <Table>
                <TableCaption className="sr-only">Riwayat komisi</TableCaption>
                <TableHeader>
                  <tr>
                    <TableHead>Tanggal</TableHead>
                    <TableHead className="text-right">Nilai</TableHead>
                    <TableHead>Status</TableHead>
                  </tr>
                </TableHeader>
                <TableBody>
                  {referral.commissions.map((c) => (
                    <TableRow key={c.order_id}>
                      <TableCell className={cn(dataClass, "whitespace-nowrap")}>
                        {formatDate(c.created_at)}
                      </TableCell>
                      <TableCell className={cn(dataClass, "text-right whitespace-nowrap text-fg-0")}>
                        {formatRupiah(c.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge tone={referralCommissionStatusTone(c.status)}>
                          {referralCommissionStatusLabel(c.status)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
            <ul
              aria-labelledby={COMMISSIONS_HEADING_ID}
              className="divide-y divide-line border-y border-line sm:hidden"
            >
              {referral.commissions.map((c) => (
                <li key={c.order_id} className="flex flex-col items-start gap-2 py-3">
                  <div className="flex w-full items-baseline justify-between gap-4">
                    <span className={cn(dataClass, "text-sm text-fg-2")}>{formatDate(c.created_at)}</span>
                    <span className={cn(dataClass, "whitespace-nowrap text-fg-0")}>
                      {formatRupiah(c.amount)}
                    </span>
                  </div>
                  <Badge tone={referralCommissionStatusTone(c.status)}>
                    {referralCommissionStatusLabel(c.status)}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-fg-2">
            Komisi dicairkan manual oleh ahcut. Untuk pencairan, hubungi{" "}
            <a className={textLinkClass} href={supportMailto("Pencairan komisi referral")}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </>
      )}
    </>
  );
}
